import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS = {
  green: ['Matched', 'bg-emerald-100 text-emerald-800 border-emerald-300'],
  amber: ['Not verified', 'bg-amber-100 text-amber-800 border-amber-300'],
  red: ['Unexplained', 'bg-rose-100 text-rose-800 border-rose-300'],
  none: ['No card activity', 'bg-muted text-muted-foreground'],
};
const ROW_BG = { green: 'bg-emerald-50/60', amber: 'bg-amber-50/60', red: 'bg-rose-50/60', none: '' };

const StatusBadge = ({ s, id }) => (
  <Badge variant="outline" className={`text-[10px] ${STATUS[s][1]}`} data-testid={id}>{STATUS[s][0]}</Badge>
);

const COLS = ['Bank group', 'Total sales', 'Cash', 'Card sales', 'Expected', 'Actual settlement', 'Fees', 'VAT on fees',
  'Refunds / chargebacks', 'Timing in / out', 'Difference', 'Status'];

function GroupRow({ r, idx, month }) {
  const [open, setOpen] = useState(false);
  const tid = `svb-m-${month}-${idx}`;
  return (
    <>
      <tr className={`${ROW_BG[r.status]} cursor-pointer hover:brightness-95`} onClick={() => setOpen(!open)} data-testid={`${tid}-row`}>
        <td className="p-1.5 pl-5">
          <span className="inline-flex items-center gap-1">{open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}{r.group}</span>
        </td>
        <td className="p-1.5 text-right">{r.total_sales ? fmt(r.total_sales) : '—'}</td>
        <td className="p-1.5 text-right">{r.cash_sales ? fmt(r.cash_sales) : '—'}</td>
        <td className="p-1.5 text-right">{fmt(r.card_sales)}</td>
        <td className="p-1.5 text-right">{fmt(r.expected_settlement)}</td>
        <td className="p-1.5 text-right">{fmt(r.actual_settlement)}</td>
        <td className="p-1.5 text-right">{fmt(r.fees)}{r.fee_pct != null && <span className="text-muted-foreground"> ({r.fee_pct}%)</span>}</td>
        <td className="p-1.5 text-right">{fmt(r.vat_on_fees)}</td>
        <td className="p-1.5 text-right">{fmt(r.refunds + r.chargebacks)}</td>
        <td className="p-1.5 text-right text-muted-foreground">+{fmt(r.timing_in_from_next_month)} / −{fmt(r.timing_out_to_previous_month)}</td>
        <td className={`p-1.5 text-right font-semibold ${r.status === 'red' ? 'text-rose-700' : r.status === 'green' ? 'text-emerald-700' : ''}`} data-testid={`${tid}-diff`}>{fmt(r.difference)}</td>
        <td className="p-1.5"><StatusBadge s={r.status} id={`${tid}-status`} /></td>
      </tr>
      {r.banks.filter((b) => b.missing_days).map((b) => (
        <tr key={b.account_id} className="bg-amber-50 text-amber-800" data-testid={`${tid}-missing-${b.account_id.slice(0, 8)}`}>
          <td className="p-1 pl-9" colSpan={10}>Statement missing — {b.name}: {b.missing_days} day(s) of this month have no statement uploaded. Not counted as a confirmed difference.</td>
          <td className="p-1" colSpan={2}><StatusBadge s="amber" id={`${tid}-missing-badge-${b.account_id.slice(0, 8)}`} /></td>
        </tr>
      ))}
      {open && (
        <tr data-testid={`${tid}-drill`}>
          <td colSpan={12} className="p-2 bg-background border-y">
            <div className="text-[11px] space-y-1.5">
              <div><span className="font-semibold">Why:</span> {r.reason}{r.notes?.length ? ` · ${r.notes.join(' · ')}` : ''}</div>
              <div><span className="font-semibold">Branches:</span> {r.branches.join(', ') || '—'}</div>
              <div className="flex flex-wrap gap-3">
                {r.banks.map((b) => (
                  <span key={b.account_id} className={b.missing_days ? 'text-amber-700' : 'text-emerald-700'}>
                    {b.name}: settled {fmt(b.settlements)} · {b.missing_days ? `${b.missing_days} day(s) without statement` : 'statement complete'}
                  </span>
                ))}
              </div>
              <div className="text-muted-foreground">Net received after fees and VAT: {fmt(r.net_received)}. Settlements are matched to the sale day before they reach the bank.</div>
              {r.bank_transfer_sales > 0 && (
                <div className="text-muted-foreground" data-testid={`${tid}-transfer-sales`}>Invoice / loan-recovery sales paid by bank transfer, not by card ({fmt(r.bank_transfer_sales)}), are left out of card sales.</div>
              )}
              {r.daily.length > 0 && (
                <div className="max-h-56 overflow-auto border rounded">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0"><tr>
                      <th className="text-left p-1">Sale date</th><th className="text-right p-1">Card sales</th>
                      <th className="text-right p-1">Settled (after refunds)</th><th className="text-right p-1">Difference</th>
                    </tr></thead>
                    <tbody>{r.daily.map((d) => (
                      <tr key={d.sale_date} className={Math.abs(d.difference) > 1 ? 'bg-warning/5' : ''}>
                        <td className="p-1 font-mono">{d.sale_date}</td><td className="p-1 text-right">{fmt(d.card_sales)}</td>
                        <td className="p-1 text-right">{fmt(d.net_settlement)}</td>
                        <td className={`p-1 text-right ${d.difference < -1 ? 'text-rose-600' : d.difference > 1 ? 'text-emerald-700' : ''}`}>{fmt(d.difference)}</td>
                      </tr>))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SalesVsBankMonthly({ lagDays }) {
  const y = new Date().getFullYear();
  const [from, setFrom] = useState(`${y}-01`);
  const [to, setTo] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const last = useRef('');

  const load = async () => {
    const key = `${from}|${to}|${lagDays}`;
    last.current = key;
    setLoading(true);
    try {
      const r = await api.get('/sales/vs-bank/monthly', { params: { start_month: from, end_month: to, lag_days: lagDays } });
      if (last.current === key) setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load monthly reconciliation');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to, lagDays]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2" data-testid="sales-vs-bank-monthly">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span>From</span>
        <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 px-2 border rounded-md bg-background" data-testid="svb-monthly-from" />
        <span>to</span>
        <input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 px-2 border rounded-md bg-background" data-testid="svb-monthly-to" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-7 px-2" data-testid="svb-monthly-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
        </Button>
        <span className="text-muted-foreground">Read-only · no terminal links needed · duplicate statement lines counted once</span>
      </div>
      <div className="text-[10px] text-muted-foreground flex flex-wrap gap-3" data-testid="svb-monthly-legend">
        <span><StatusBadge s="green" id="svb-legend-green" /> {data?.rules?.green}</span>
        <span><StatusBadge s="amber" id="svb-legend-amber" /> {data?.rules?.amber}</span>
        <span><StatusBadge s="red" id="svb-legend-red" /> {data?.rules?.red}</span>
      </div>
      <div className="max-h-[32rem] overflow-auto rounded-lg border">
        <table className="w-full text-[11px]" data-testid="svb-monthly-table">
          <thead className="bg-muted/60 sticky top-0 z-10">
            <tr>{COLS.map((c, i) => <th key={c} className={`p-1.5 ${i && i < 11 ? 'text-right' : 'text-left'}`}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {loading && !data && <tr><td colSpan={12} className="p-3 text-center text-muted-foreground">Loading…</td></tr>}
            {(data?.months || []).map((m) => (
              <MonthBlock key={m.month} m={m} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthBlock({ m }) {
  const c = m.company;
  return (
    <>
      <tr className={`font-semibold border-t-2 ${ROW_BG[c.status]}`} data-testid={`svb-m-${m.month}-company`}>
        <td className="p-1.5">{m.month} · Company</td>
        <td className="p-1.5 text-right">{fmt(c.total_sales)}</td>
        <td className="p-1.5 text-right">{fmt(c.cash_sales)}</td>
        <td className="p-1.5 text-right">{fmt(c.card_sales)}</td>
        <td className="p-1.5 text-right">{fmt(c.card_sales)}</td>
        <td className="p-1.5 text-right">{fmt(c.actual_settlement)}</td>
        <td className="p-1.5 text-right">{fmt(c.fees)}</td>
        <td className="p-1.5 text-right">{fmt(c.vat_on_fees)}</td>
        <td className="p-1.5 text-right">{fmt(c.refunds + c.chargebacks)}</td>
        <td className="p-1.5 text-right text-muted-foreground">—</td>
        <td className="p-1.5 text-right">
          {fmt(c.difference)}
          <div className="text-[10px] font-normal text-muted-foreground">unexplained {fmt(c.unexplained)} · not verified {fmt(c.unverified)}</div>
        </td>
        <td className="p-1.5"><StatusBadge s={c.status} id={`svb-m-${m.month}-company-status`} /></td>
      </tr>
      {m.rows.filter((r) => r.status !== 'none').map((r, i) => <GroupRow key={r.group} r={r} idx={i} month={m.month} />)}
    </>
  );
}
